import React, { useState, useEffect, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { Menu, Transition } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/20/solid';
import { ChartBarIcon } from '@heroicons/react/24/outline';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { apiClient } from '../../services/apiClient';

const RequestStats = () => {
  const { t, i18n } = useTranslation();
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedYearMonthly, setSelectedYearMonthly] = useState(new Date().getFullYear());
  const [dailyStats, setDailyStats] = useState(null);
  const [monthlyStats, setMonthlyStats] = useState(null);
  const [availableMonths, setAvailableMonths] = useState([]);
  const [isDailyLoading, setIsDailyLoading] = useState(true);
  const [isMonthlyLoading, setIsMonthlyLoading] = useState(true);
  const [dailyError, setDailyError] = useState(null);
  const [monthlyError, setMonthlyError] = useState(null);

  // Function to format date for X-axis
  const formatDate = (date) => {
    return new Date(date).toLocaleDateString(i18n.language, { day: 'numeric', month: 'short' });
  };

  // Function to format month for X-axis
  const formatMonth = (month) => {
    return new Date(2000, month - 1, 1).toLocaleDateString(i18n.language, { month: 'long' });
  };

  // Custom tooltip that includes total
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const total = payload.reduce((sum, entry) => sum + (parseInt(entry.value) || 0), 0);
      
      // Format the date for display
      const displayDate = typeof label === 'string' && label.includes('T')
        ? new Date(label).toLocaleDateString(i18n.language, { day: 'numeric', month: 'long', year: 'numeric' })
        : label;

      return (
        <div className="bg-white p-3 border border-gray-200 rounded shadow-lg">
          <p className="font-semibold">{displayDate}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ color: entry.color }}>
              {t(`statistics.requestTypes.${entry.dataKey}`)}: {parseInt(entry.value) || 0}
            </p>
          ))}
          <p className="font-semibold mt-2 border-t pt-2">
            {t('statistics.total')}: {total}
          </p>
        </div>
      );
    }
    return null;
  };

  // Function to get month/year options
  const getMonthYearOptions = () => {
    if (!availableMonths) return [];
    return availableMonths.map(({ month, year }) => ({
      value: `${year}-${month}`,
      label: `${formatMonth(month)} ${year}`
    }));
  };

  // Function to get available years
  const getAvailableYears = () => {
    if (!availableMonths) return [];
    const years = [...new Set(availableMonths.map(({ year }) => year))];
    return years.sort((a, b) => b - a); // Sort descending
  };

  // Function to prepare data for daily chart
  const prepareDailyData = () => {
    if (!dailyStats || !Array.isArray(dailyStats)) return [];
    
    // Group by date
    const groupedByDate = dailyStats.reduce((acc, curr) => {
      const date = curr.date;
      if (!acc[date]) {
        acc[date] = { date };
      }
      acc[date][curr.type] = parseInt(curr.count) || 0;
      return acc;
    }, {});

    // Convert to array and sort by date
    return Object.values(groupedByDate)
      .sort((a, b) => new Date(a.date) - new Date(b.date));
  };

  // Function to prepare data for monthly chart
  const prepareMonthlyData = () => {
    if (!monthlyStats || !Array.isArray(monthlyStats)) return [];
    
    // Group by month
    const groupedByMonth = monthlyStats.reduce((acc, curr) => {
      if (!acc[curr.month]) {
        acc[curr.month] = { 
          month: formatMonth(curr.month),
          date: new Date(selectedYearMonthly, curr.month - 1, 1)
        };
      }
      acc[curr.month][curr.type] = parseInt(curr.count) || 0;
      return acc;
    }, {});

    // Convert to array and sort by month
    return Object.values(groupedByMonth)
      .sort((a, b) => a.date - b.date)
      .map(({ date, ...rest }) => rest);
  };

  // Define colors for request types
  const typeColors = {
    translate: '#ea4335',  // red
    improve: '#34a853',    // green
    format: '#4285f4',     // blue
    mail: '#fbbc05',        // yellow/orange
    report: '#7f1ac6',      // purple
    lamal: '#61dade',       // cyan
    dictaphone: '#ffb366'   // pastel orange
  };

  // Function to sort request types by total count
  const getSortedRequestTypes = (stats) => {
    if (!stats || !stats.length) return [];
    
    const typeTotals = stats.reduce((acc, curr) => {
      const type = curr.type;
      const count = parseInt(curr.count) || 0;
      acc[type] = (acc[type] || 0) + count;
      return acc;
    }, {});

    return Object.entries(typeTotals)
      .sort(([, a], [, b]) => b - a)
      .map(([type]) => type);
  };

  // Empty state component
  const EmptyState = ({ message }) => (
    <div className="flex flex-col items-center justify-center h-64 text-gray-500">
      <ChartBarIcon className="w-16 h-16 mb-4" />
      <p className="text-lg">{message}</p>
    </div>
  );

  // Fetch daily statistics
  useEffect(() => {
    const fetchDailyStats = async () => {
      try {
        setIsDailyLoading(true);
        setDailyError(null);
        const data = await apiClient.get(
          `/api/statistics/daily?month=${selectedMonth}&year=${selectedYear}`
        );

        setDailyStats(Array.isArray(data.dailyStats) ? data.dailyStats : []);
        setAvailableMonths(Array.isArray(data.availableMonths) ? data.availableMonths : []);
      } catch (err) {
        console.error('Error fetching daily statistics:', err);
        setDailyError(t('statistics.errors.fetchFailed'));
        setDailyStats([]);
        setAvailableMonths([]);
      } finally {
        setIsDailyLoading(false);
      }
    };

    fetchDailyStats();
  }, [selectedMonth, selectedYear, t]);

  // Fetch monthly statistics
  useEffect(() => {
    const fetchMonthlyStats = async () => {
      try {
        setIsMonthlyLoading(true);
        setMonthlyError(null);
        const data = await apiClient.get(
          `/api/statistics/monthly?year=${selectedYearMonthly}`
        );

        setMonthlyStats(Array.isArray(data.monthlyStats) ? data.monthlyStats : []);
      } catch (err) {
        console.error('Error fetching monthly statistics:', err);
        setMonthlyError(t('statistics.errors.fetchFailed'));
        setMonthlyStats([]);
      } finally {
        setIsMonthlyLoading(false);
      }
    };

    fetchMonthlyStats();
  }, [selectedYearMonthly, t]);

  const monthYearOptions = getMonthYearOptions();
  const dailyData = prepareDailyData();
  const monthlyData = prepareMonthlyData();
  const availableYears = getAvailableYears();
  const sortedRequestTypes = getSortedRequestTypes([...(dailyStats || []), ...(monthlyStats || [])]);

  return (
    <div className="space-y-8">
      {/* Daily Stats */}
      <div className="bg-gray-100 rounded-lg">
        <div className="px-6 py-4">
          <h3 className="text-lg font-medium text-gray-900">
            {t('statistics.dailyRequests')}
          </h3>
        </div>
        <div className="px-6 py-4">
          <div className="flex justify-end mb-4">
            <Menu as="div" className="relative inline-block text-left">
              {({ open }) => (
                <>
                  <Menu.Button className="inline-flex w-56 justify-between items-center rounded-md bg-[rgb(197,214,248)] px-4 py-2 text-sm font-medium text-gray-700 hover:bg-[rgb(187,204,238)] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-100">
                    {monthYearOptions.find(option => option.value === `${selectedYear}-${selectedMonth}`)?.label}
                    <ChevronDownIcon 
                      className={`ml-2 h-5 w-5 text-gray-500 transition-transform duration-200 ${
                        open ? 'transform rotate-180' : ''
                      }`} 
                      aria-hidden="true" 
                    />
                  </Menu.Button>

                  <Transition
                    as={Fragment}
                    enter="transition ease-out duration-100"
                    enterFrom="opacity-0 scale-95"
                    enterTo="opacity-100 scale-100"
                    leave="transition ease-in duration-75"
                    leaveFrom="opacity-100 scale-100"
                    leaveTo="opacity-0 scale-95"
                  >
                    <Menu.Items className="absolute right-0 z-10 mt-2 w-56 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                      <div className="py-1">
                        {monthYearOptions.map((option) => (
                          <Menu.Item key={option.value}>
                            {({ active }) => (
                              <button
                                onClick={() => {
                                  const [year, month] = option.value.split('-');
                                  setSelectedYear(parseInt(year));
                                  setSelectedMonth(parseInt(month));
                                }}
                                className={`${
                                  active ? 'bg-[rgb(197,214,248)]' : ''
                                } ${
                                  option.value === `${selectedYear}-${selectedMonth}` ? 'bg-[rgb(197,214,248)]' : ''
                                } group flex w-full items-center px-4 py-2 text-sm text-gray-700`}
                              >
                                {option.label}
                              </button>
                            )}
                          </Menu.Item>
                        ))}
                      </div>
                    </Menu.Items>
                  </Transition>
                </>
              )}
            </Menu>
          </div>
          <div className="h-80">
            {isDailyLoading ? (
              <div className="flex items-center justify-center h-full">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
              </div>
            ) : dailyError ? (
              <div className="text-red-600 p-4 text-center">
                {dailyError}
              </div>
            ) : dailyData.length === 0 ? (
              <EmptyState message={t('statistics.noData')} />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={dailyData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" tickFormatter={formatDate} />
                  <YAxis />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  {sortedRequestTypes.map(type => (
                    <Bar
                      key={type}
                      dataKey={type}
                      name={t(`statistics.requestTypes.${type}`)}
                      stackId="a"
                      fill={typeColors[type]}
                    />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>

      {/* Monthly Stats */}
      <div className="bg-gray-100 rounded-lg">
        <div className="px-6 py-4">
          <h3 className="text-lg font-medium text-gray-900">
            {t('statistics.monthlyRequests')}
          </h3>
        </div>
        <div className="px-6 py-4">
          <div className="flex justify-end mb-4">
            <Menu as="div" className="relative inline-block text-left">
              {({ open }) => (
                <>
                  <Menu.Button className="inline-flex w-56 justify-between items-center rounded-md bg-[rgb(197,214,248)] px-4 py-2 text-sm font-medium text-gray-700 hover:bg-[rgb(187,204,238)] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-100">
                    {selectedYearMonthly}
                    <ChevronDownIcon 
                      className={`ml-2 h-5 w-5 text-gray-500 transition-transform duration-200 ${
                        open ? 'transform rotate-180' : ''
                      }`} 
                      aria-hidden="true" 
                    />
                  </Menu.Button>

                  <Transition
                    as={Fragment}
                    enter="transition ease-out duration-100"
                    enterFrom="opacity-0 scale-95"
                    enterTo="opacity-100 scale-100"
                    leave="transition ease-in duration-75"
                    leaveFrom="opacity-100 scale-100"
                    leaveTo="opacity-0 scale-95"
                  >
                    <Menu.Items className="absolute right-0 z-10 mt-2 w-56 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                      <div className="py-1">
                        {availableYears.map((year) => (
                          <Menu.Item key={year}>
                            {({ active }) => (
                              <button
                                onClick={() => setSelectedYearMonthly(year)}
                                className={`${
                                  active ? 'bg-[rgb(197,214,248)]' : ''
                                } ${
                                  year === selectedYearMonthly ? 'bg-[rgb(197,214,248)]' : ''
                                } group flex w-full items-center px-4 py-2 text-sm text-gray-700`}
                              >
                                {year}
                              </button>
                            )}
                          </Menu.Item>
                        ))}
                      </div>
                    </Menu.Items>
                  </Transition>
                </>
              )}
            </Menu>
          </div>
          <div className="h-80">
            {isMonthlyLoading ? (
              <div className="flex items-center justify-center h-full">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
              </div>
            ) : monthlyError ? (
              <div className="text-red-600 p-4 text-center">
                {monthlyError}
              </div>
            ) : monthlyData.length === 0 ? (
              <EmptyState message={t('statistics.noData')} />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={monthlyData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  {sortedRequestTypes.map(type => (
                    <Bar
                      key={type}
                      dataKey={type}
                      name={t(`statistics.requestTypes.${type}`)}
                      stackId="a"
                      fill={typeColors[type]}
                    />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RequestStats; 